import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { backendURL } from 'config';
import { headerJson } from 'utilities/common';
import { EmptyState, LoadingOverlay } from 'dashboard/components/common';
import ProductDialog from './ProductDialog';

const STATUS_COLOR = {
  active: 'success',
  archived: 'default',
  draft: 'warning',
};

/**
 * Catalog tab. Lists products; CSV import/export sits in the toolbar
 * because most brands seed their catalog from a spreadsheet.
 */
export default function ProductsTab({ brandId }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const importInputRef = useRef(null);

  const reload = useCallback(() => {
    if (!brandId) return Promise.resolve();
    setLoading(true);
    return API.listShopProducts({ brand_id: brandId })
      .then((res) => setProducts(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [brandId]);

  useEffect(() => {
    if (!brandId) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listShopProducts({ brand_id: brandId })
      .then((res) => { if (!cancelled) setProducts(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load', { toastId: 'products-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [brandId]);

  const openEdit = async (row) => {
    try {
      const full = await API.getShopProduct({ brand_id: brandId, _id: row._id });
      setEditing(full);
      setDlgOpen(true);
    } catch (err) {
      toast.error(err?.message || 'Failed to load product');
    }
  };

  const remove = (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    API.deleteShopProduct({ brand_id: brandId, _id: row._id })
      .then(() => { toast.success('Deleted'); reload(); })
      .catch((err) => toast.error(err?.message || 'Failed to delete'));
  };

  const downloadTemplate = () => {
    const csv = [
      'name,slug,description,category,base_price,currency,status,tags,image_url,tax_code,variant_sku,variant_attributes,variant_price,variant_active',
      'Acme T-shirt,acme-tshirt,Soft cotton tee,apparel,29.00,USD,active,"summer,bestseller",https://example.com/tshirt.jpg,*,,,,',
      'Acme T-shirt,acme-tshirt,,,,,,,,,ACME-TS-M-BLK,Size:M|Color:Black,29.00,true',
      'Acme T-shirt,acme-tshirt,,,,,,,,,ACME-TS-L-BLK,Size:L|Color:Black,29.00,true',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'products-template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const handleCsvExport = async () => {
    if (!brandId) return;
    try {
      const res = await axios.post(
        `${backendURL}/dashboard/brand/shop/products/export-csv`,
        { brand_id: brandId },
        { headers: headerJson(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products-${String(brandId).slice(-6)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Export failed');
    }
  };

  const handleCsvUpload = async (file) => {
    if (!file || !brandId) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('brand_id', brandId);
      fd.append('file', file);
      const res = await API.importShopProductsCsv(fd);
      setImportResult(res);
      toast.success(`Imported · ${res?.inserted || 0} new · ${res?.updated || 0} updated`);
      reload();
    } catch (err) {
      toast.error(err?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box>
      <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap='10px' mb='14px'>
        <Box>
          <Typography fontWeight='700'>Products</Typography>
          <Typography variant='body2' color='text.secondary'>
            The catalog. Each product can have one or many SKUs (variants).
          </Typography>
        </Box>
        <Stack direction='row' spacing='8px' flexWrap='wrap'>
          <input
            ref={importInputRef}
            type='file'
            accept='.csv,text/csv'
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleCsvUpload(f);
              e.target.value = '';
            }}
          />
          <Button size='small' variant='outlined' onClick={downloadTemplate}>Template</Button>
          <Button
            size='small'
            variant='outlined'
            onClick={() => importInputRef.current?.click()}
            disabled={importing}
          >
            {importing ? 'Importing…' : 'Import CSV'}
          </Button>
          <Button
            size='small'
            variant='outlined'
            onClick={handleCsvExport}
            disabled={products.length === 0}
          >
            Export CSV
          </Button>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => { setEditing(null); setDlgOpen(true); }}
          >
            New product
          </Button>
        </Stack>
      </Stack>

      {importResult && (
        <Box mb='10px' p='10px' borderRadius='10px' border='1px solid' borderColor='divider'>
          <Typography variant='body2' fontWeight='600'>
            CSV import: {importResult.inserted} added · {importResult.updated} updated
            {(importResult.variants_inserted || importResult.variants_updated) ? (
              ` · ${importResult.variants_inserted || 0} new variant${importResult.variants_inserted === 1 ? '' : 's'} · ${importResult.variants_updated || 0} updated variant${importResult.variants_updated === 1 ? '' : 's'}`
            ) : ''}
            {importResult.errors?.length ? ` · ${importResult.errors.length} errors` : ''}
          </Typography>
          {importResult.errors?.length > 0 && (
            <Box mt='6px' maxHeight='120px' overflow='auto'>
              {importResult.errors.slice(0, 10).map((e, idx) => (
                <Typography key={idx} variant='caption' color='error.main' display='block'>
                  Row {e.row}: {e.reason}
                </Typography>
              ))}
              {importResult.errors.length > 10 && (
                <Typography variant='caption' color='text.secondary'>
                  … and {importResult.errors.length - 10} more
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      <LoadingOverlay loading={loading && products.length === 0} minHeight={180}>
        {!loading && products.length === 0 && <EmptyState>No products yet.</EmptyState>}
        {products.map((p) => (
        <Stack
          key={p._id}
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          flexWrap='wrap'
          gap='10px'
          sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: '12px',
            p: '14px', mb: '10px',
          }}
        >
          <Box>
            <Stack direction='row' spacing='6px' alignItems='center' flexWrap='wrap'>
              <Typography fontWeight='700'>{p.name}</Typography>
              <Chip size='small' label={p.status} color={STATUS_COLOR[p.status] || 'default'} />
              {p.category && <Chip size='small' variant='outlined' label={p.category} />}
            </Stack>
            <Typography variant='body2' color='text.secondary'>
              {p.base_price} {p.currency} · /{p.slug}
            </Typography>
          </Box>
          <Stack direction='row' spacing='4px'>
            <Tooltip title='Edit'>
              <IconButton size='small' onClick={() => openEdit(p)}>
                <EditIcon fontSize='small' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Delete'>
              <IconButton size='small' color='error' onClick={() => remove(p)}>
                <DeleteIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        ))}
      </LoadingOverlay>
      <ProductDialog
        open={dlgOpen}
        onClose={() => setDlgOpen(false)}
        brandId={brandId}
        editing={editing}
        onSaved={reload}
      />
    </Box>
  );
}
